import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { AirportGateway } from 'src/app/application/gateway/AirportGateway';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { ConsigneeGateway } from 'src/app/application/gateway/ConsigneeGateway';
import { CountryGateway } from 'src/app/application/gateway/CountryGateway';
import { DirectSaleProgramGateway } from 'src/app/application/gateway/DirectSaleProgramGateway';
import { NotifierGateway } from 'src/app/application/gateway/NotifierGateway';
import { ProfileGateway } from 'src/app/application/gateway/ProfileGateway';
import { Consignee } from 'src/app/domain/models/Consignee';
import { Notifier } from 'src/app/domain/models/Notifier';
import { Port } from 'src/app/domain/models/Port';
import { Importer } from 'src/app/domain/models/Profile';
import { BasicSelect } from '../../util/select-utils';
import { transportTypes } from '../../util/transportType.Utils';
import { Airport } from 'src/app/domain/models/Airport';
import { formLimit } from '../../util/form-limit';
import { PortGateway } from 'src/app/application/gateway/PortGateway';
import { Country } from 'src/app/domain/models/Country';
import { Species } from 'src/app/domain/models/Species';
import { Variety } from 'src/app/domain/models/Variety';
import { VarietyGateway } from 'src/app/application/gateway/VarietyGateway';
import { SpeciesGateway } from 'src/app/application/gateway/SpeciesGateway';
import { ExportOfferUtils } from '../../util/export-offer-utils';
import { BasicUser } from 'src/app/domain/models/User';
import { CreateProgramFromStockRequest } from 'src/app/domain/models/DirectSaleProgram';
import { CityService } from 'src/app/application/service/CityService';
import { City } from 'src/app/domain/models/City';

@Component({
  selector: 'app-program-from-stock-form',
  templateUrl: './program-from-stock-form.component.html',
  styleUrls: ['./program-from-stock-form.component.css']
})
export class ProgramFromStockFormComponent implements OnInit {

  formGroup: FormGroup = this._formBuilder.group({
    speciesId: [null, Validators.required],
    varietyId: [null, Validators.required],
    type: [0, Validators.required],
    importerId: [null, Validators.required],
    consigneeId: [null, Validators.required],
    importToCountryId: [null, Validators.required],
    notifyId: [null, Validators.required],
    portToId: [null, Validators.required],
    airportId: [null],
    incoterms: ['EXW', Validators.required],
    programName: [null],
    cityId: [null],
  });

  loading: boolean = false;
  submit: boolean = false;
  programType: number = 0;

  importers: Importer[] = []
  consignees: Consignee[] = []
  notifies: Notifier[] = []
  countries: Country[] = []
  ports: Port[] = []
  airports: Airport[] = []
  species: Species[] = []
  varieties: Variety[] = []
  cities: City[] = []

  profile: BasicUser | undefined

  formLimit = formLimit

  @Output()
  OnCreateStock: EventEmitter<any> = new EventEmitter();

  @Output()
  OnCancelCreate:  EventEmitter<any> = new EventEmitter();

  programTypes: BasicSelect[] = transportTypes
  selectedCountryTo: Country | undefined
  selectedSpecies: Species | undefined
  selectedVariety: Variety | undefined

  incotermsOptions: any[] = []

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _messageService: MessageService,
    public translate: TranslateService,
    private _authService: AuthGateway,
    private _formBuilder: FormBuilder,
    private _directSaleService: DirectSaleProgramGateway,
    private _airportService: AirportGateway,
    private _countryService: CountryGateway,
    private _consigneeService: ConsigneeGateway,
    private _notifyService: NotifierGateway,
    private _profileService: ProfileGateway,
    private _portService: PortGateway,
    private _varietyService: VarietyGateway,
    private _speciesService: SpeciesGateway,
    private _cityService: CityService,
  ) { }

  ngOnInit(): void {
    this.profile = this._authService.getUser()
    this.getImporters()
    this.getSpecies()
    this.getCountries()
  }

  @Input()
  stockId!: number

  getAirPorts(): void {
    this.selectedCountryTo = this.countries.find(x => x.id == this.formGroup.controls['importToCountryId'].value)

    if(this.formGroup.controls['importToCountryId'].value) {
      this._airportService.getDestinationAirportsByCountry(this.formGroup.controls['importToCountryId'].value).subscribe({
        next: (v) => {
          this.airports = v
        },
        error: (e) => {
          console.log(e)
          this.loading = false
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Port.DestinationPortManyRetrieveError') })
        },
        complete: () => console.info('Destination Ports query successfully')
      })
    }
  }

  onCancelCreation(): void {
    this.OnCancelCreate.emit()
  }

  getImporters(): void {
    this.importers = []
    this.loading = true
    if(this.profile) {
      this._profileService.getAllImportersByExporterId().subscribe({
        next: (v) => {
          this.importers = v
          this.incotermsOptions = ExportOfferUtils.getIncotermsClosedSaleOptions()
          this.loading = false
        },
        error: (e) => {
          console.log(e)
          this.loading = false
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Importer.ManyRetrieveError') })
        },
        complete: () => console.info('Importers query successfully')
      })
    }
  }

  getConsignees(): void {
    if(this.formGroup.controls['importerId'].value) {
      this._consigneeService.getAllForClosedSale(this.formGroup.controls['importerId'].value).subscribe({
        next: (v) => {
          this.consignees = v
          this.loading = false
        },
        error: (e) => {
          console.log(e)
          this.loading = false
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Consignee.ManyRetrieveError') })
        },
        complete: () => console.info('Consignees query successfully')
      })
    }
  }

  getNotifies(): void {
    if(this.formGroup.controls['importerId'].value) {
      this._notifyService.getAllForClosedSale(this.formGroup.controls['importerId'].value).subscribe({
        next: (v) => {
          this.notifies = v
          this.loading = false
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Notify.ManyRetrieveError') })
        },
        complete: () => console.info('Notifies query successfully')
      })
    }
  }

  getSpecies(): void {
    this._speciesService.getAllByProductType(1).subscribe({
      next: (v) => {
        this.species = v
        this.loading = false
      },
      error: (e) => {
        console.log(e)
        this.loading = false
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Species.ManyRetrieveError') })
      },
      complete: () => console.info('Species query successfully')
    })
  }

  onChangeSpecies(): void {
    this.selectedSpecies = this.species.find(x => x.id === this.formGroup.controls['speciesId'].value)
    this.getVarieties()
  }

  onChangeVariety(): void {
    this.selectedVariety = this.varieties.find(x => x.id === this.formGroup.controls['varietyId'].value)
  }

  onChangeIncoterm(event: any): void {
    // Puede agregar lógica adicional si es necesaria
  }

  onChangeImporter(): void {
    this.getConsignees()
    this.getNotifies()
  }

  onChangeImportToCountry(): void {
    this.programType = this.formGroup.controls['type'].value
    if(this.programType === 0) {
      this.formGroup.controls['portToId'].setValidators([Validators.required])
      this.formGroup.controls['airportId'].clearValidators()
      this.formGroup.controls['cityId'].clearValidators()
    } else if(this.programType === 1) {
      this.formGroup.controls['airportId'].setValidators([Validators.required])
      this.formGroup.controls['portToId'].clearValidators()
      this.formGroup.controls['cityId'].clearValidators()
    } else if(this.programType === 2) {
      this.formGroup.controls['cityId'].setValidators([Validators.required])
      this.formGroup.controls['portToId'].clearValidators()
      this.formGroup.controls['airportId'].clearValidators()
    }
    this.formGroup.controls['portToId'].updateValueAndValidity()
    this.formGroup.controls['airportId'].updateValueAndValidity()
    this.formGroup.controls['cityId'].updateValueAndValidity()
    this.getPorts()
    this.getAirPorts()
    this.getCities()
  }

  getVarieties(): void {
    if(this.formGroup.controls['speciesId'].value) {
      this.varieties = []
      this._varietyService.getAllBySpecies(this.formGroup.controls['speciesId'].value).subscribe({
        next: (v) => {
          this.varieties = v
          this.loading = false
        },
        error: (e) => {
          console.log(e)
          this.loading = false
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Variety.ManyRetrieveError') })
        },
        complete: () => console.info('Variety query successfully')
      })
    }
  }

  getCountries(): void {
    this.countries = []
    this._countryService.getAll().subscribe({
      next: (v) => {
        this.countries = v
        this.loading = false
      },
      error: (e) => {
        console.log(e)
        this.loading = false
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Country.ManyRetrieveError') })
      },
      complete: () => console.info('Country query successfully')
    })
  }

  getPorts(): void {
    this.selectedCountryTo = this.countries.find(x => x.id == this.formGroup.controls['importToCountryId'].value)

    if(this.formGroup.controls['importToCountryId'].value) {
      this._portService.getDestinationPortsByCountry(this.formGroup.controls['importToCountryId'].value).subscribe({
        next: (v) => {
          this.ports = v
        },
        error: (e) => {
          console.log(e)
          this.loading = false
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Port.DestinationPortManyRetrieveError') })
        },
        complete: () => console.info('Destination Ports query successfully')
      })
    }
  }

  getCities(): void {
    if(this.formGroup.controls['importToCountryId'].value) {
      this._cityService.getByCountry(this.formGroup.controls['importToCountryId'].value).subscribe({
        next: (v) => {
          this.cities = v;
        },
        error: (e) => {
          this.cities = [];
          this.loading = false;
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.City.ManyRetrieveError') })
        },
        complete: () => console.info('Cities query successfully')
      })
    }
  }


  save(): void {
    this.submit = true

    if(this.formGroup.valid)
    {
      var destinationId = this.formGroup.controls['type'].value == 0 ?
        this.formGroup.controls['portToId'].value :
        this.formGroup.controls['type'].value == 1 ? this.formGroup.controls['airportId'].value : this.formGroup.controls['cityId'].value

      var request: CreateProgramFromStockRequest = {
      stockId: this.stockId,
      destinationId: destinationId,
      ... this.formGroup.value
      }
      this.loading = true
      // Aquí va la lógica de guardado
      this._directSaleService.createProgramFromStock(request).subscribe({
        next: (v) => {
          this.loading = false
          this._messageService.add({ severity: 'success',
            summary: this.translate.instant('MessageService.Summary.Success'),
            detail: this.translate.instant('MessageService.ProgramFromStock.CreateProgramSuccess') })
          this.OnCreateStock.emit()
        },
        error: (e) => {
          this.loading = false
          console.log(e)
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.ProgramFromStock.CreateProgramError') })
        }
      })
      console.log('Formulario válido:', this.formGroup.value)
    }
  }
}

